import { z } from 'zod';

import { amountSchema } from './money.ts';

// Paybill (M-Pesa C2B) payments (ADR 0020; cashier-and-payments.md §2.5). Money is integer
// cents in a string. Visit and invoice numbers never travel in a URL: the bill lookup
// takes the number in the body.

export const C2B_PAYMENT_STATUSES = ['unmatched', 'matched'] as const;
export const C2B_UNMATCHED_REASON_CODES = ['no_match', 'visit_cancelled', 'nothing_owed'] as const;

export const c2bPaymentListQuerySchema = z.object({
  status: z.enum(C2B_PAYMENT_STATUSES).default('unmatched'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

export const c2bPaymentViewSchema = z.object({
  id: z.uuid(),
  transactionCode: z.string(),
  amount: amountSchema,
  paidAt: z.iso.datetime(),
  notifiedAt: z.iso.datetime(),
  /** What the payer typed, as typed. */
  accountReference: z.string(),
  status: z.enum(C2B_PAYMENT_STATUSES),
  unmatchedReason: z.enum(C2B_UNMATCHED_REASON_CODES).nullable(),
  /** The words for `unmatchedReason`. */
  unmatchedReasonText: z.string().nullable(),
  match: z
    .object({
      method: z.enum(['auto', 'manual']),
      visitId: z.uuid(),
      visitNumber: z.string(),
      matchedAt: z.iso.datetime(),
      matchedByName: z.string().nullable(),
      reason: z.string().nullable(),
      receiptId: z.uuid().nullable(),
      receiptNumber: z.string().nullable(),
    })
    .nullable(),
});
export type C2bPaymentView = z.infer<typeof c2bPaymentViewSchema>;

export const c2bPaymentListSchema = z.object({
  items: z.array(c2bPaymentViewSchema),
  nextCursor: z.uuid().nullable(),
  /** How many wait for a person at this facility, whatever page this is. */
  unmatchedCount: z.number().int().min(0),
});
export type C2bPaymentList = z.infer<typeof c2bPaymentListSchema>;

export const c2bBillLookupBodySchema = z.object({
  /** A visit number or an invoice number, as printed on the bill. */
  number: z.string().trim().min(1, 'Enter the visit or invoice number').max(60),
});

export const c2bBillCandidateSchema = z.object({
  visitId: z.uuid(),
  visitNumber: z.string(),
  visitStatus: z.enum(['open', 'closed', 'cancelled']),
  startedAt: z.iso.datetime(),
  /** Null for a counter sale to a buyer who wasn't identified. */
  patient: z.object({ name: z.string(), patientNumber: z.string() }).nullable(),
  owed: amountSchema,
});
export type C2bBillCandidate = z.infer<typeof c2bBillCandidateSchema>;

export const c2bBillLookupResultSchema = z.object({
  bill: c2bBillCandidateSchema.nullable(),
});

export const matchC2bPaymentBodySchema = z.object({
  visitId: z.uuid(),
  reason: z.string().trim().min(3, 'Say why, in a few words').max(500),
});

export const matchC2bPaymentResultSchema = z.object({
  payment: c2bPaymentViewSchema,
});

export const c2bEndpointViewSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  status: z.enum(['active', 'revoked']),
  createdAt: z.iso.datetime(),
  revokedAt: z.iso.datetime().nullable(),
});
export type C2bEndpointView = z.infer<typeof c2bEndpointViewSchema>;

export const c2bEndpointListSchema = z.object({
  items: z.array(c2bEndpointViewSchema),
  /** False until an M-Pesa adapter is configured for this server (TODO(verify-spec)). */
  connected: z.boolean(),
  /** "fake" while the server uses synthetic test notifications. */
  mode: z.enum(['none', 'fake']),
});

export const createC2bEndpointBodySchema = z.object({
  label: z.string().trim().min(1, 'Name this paybill').max(60),
});

export const createdC2bEndpointSchema = z.object({
  endpoint: c2bEndpointViewSchema,
  /** The notification path with its secret, shown once and never again. */
  confirmationPath: z.string(),
});
